/**
 * Sky on a call.
 *
 * Called by name, she comes up the way she does at sign-in — the picture at
 * full size beside a card — and stays up while the two of them talk. The
 * card holds what matters in a spoken conversation and nothing else: what she
 * heard, what she said, whether she is listening or looking something up.
 * Typing is an option pressed for, not a box waiting under every answer: a
 * person talking to her should not be looking for a text field, and one who
 * wants the keyboard is one press from it.
 *
 * It ends when they say so — "thanks", "stop", or Done — and she goes back to
 * her corner, still listening for her name.
 */
import { useEffect, useRef, useState } from "react";
import s from "./assistant.module.css";
import av from "./avatar.module.css";
import { AssistantMark } from "./AssistantMark";
import { useAssistant } from "./AssistantContext";
import { DraftCard } from "./AssistantDock";
import { greeting } from "./greeting";
import { useSession } from "../app/SessionContext";
import { Button } from "../design/components";
import { t } from "../i18n";
import { voiceLogText } from "./listen";

export function CallView() {
  const a = useAssistant();
  const session = useSession();
  const [typing, setTyping] = useState(false);
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const saidRef = useRef<HTMLDivElement>(null);

  /* A new call starts talking, not typing. */
  useEffect(() => {
    if (!a.onCall) {
      setTyping(false);
      setText("");
    }
  }, [a.onCall]);

  useEffect(() => {
    if (typing) inputRef.current?.focus();
  }, [typing]);

  const lastHer = [...a.turns].reverse().find((x) => x.role === "assistant");
  const lastYou = [...a.turns].reverse().find((x) => x.role === "operator");

  useEffect(() => {
    if (saidRef.current) saidRef.current.scrollTop = 0;
  }, [lastHer?.id]);

  if (!a.onCall) return null;

  const status = a.listening
    ? a.heard || t("Listening…")
    : a.mark === "thinking"
      ? t("Let me check…")
      : a.micBlocked
        ? t("The microphone could not be opened here. Press Type instead, or try the microphone again.")
        : a.speaking || a.conversing
          ? ""
          : t("Say “Sky” when you want me.");

  const send = () => {
    const q = text.trim();
    if (!q) return;
    setText("");
    void a.ask(q);
  };

  return (
    <div className={s.arrival} role="region" aria-label={t("Sky on a call")}>
      <AssistantMark state={a.mark} size={192} variant={a.prefs.mark} image={a.prefs.markImage} className={av.pop} />

      <div className={s.arrivalCard}>
        <div className={s.arrivalHead}>
          <p className={s.welcomeGreeting}>{greeting(session.principal.name, new Date())}</p>
        </div>

        {lastYou && <p className={s.callYou}>“{lastYou.text}”</p>}

        {lastHer ? (
          <div className={s.callHer} ref={saidRef}>
            {lastHer.text}
            {lastHer.draft && <DraftCard draft={lastHer.draft} />}
          </div>
        ) : (
          <p className={s.welcomeLine}>{t("How can I help you?")}</p>
        )}

        {status && (
          <p className={s.callStatus} aria-live="polite">
            {a.listening && <span className={s.earDot} aria-hidden="true" />} {status}
          </p>
        )}

        {typing && (
          <form
            className={s.callType}
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("Type to Sky…")}
              aria-label={t("Type to Sky")}
            />
            <Button variant="primary" size="sm" type="submit" disabled={!text.trim()}>
              {t("Send")}
            </Button>
          </form>
        )}

        <div className={s.welcomeActions}>
          {!typing ? (
            <Button variant="ghost" size="sm" onClick={() => setTyping(true)}>
              {t("Type instead")}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setTyping(false);
                a.startListening();
              }}
            >
              {t("Talk instead")}
            </Button>
          )}
          {(a.micBlocked || (!a.listening && !a.conversing && !a.speaking)) && !typing && (
            <Button variant="ghost" size="sm" onClick={() => a.startListening()}>
              {t("Listen")}
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={a.endCall}>
            {t("Done")}
          </Button>
        </div>
        {/* What her microphone did, for when she does not hear: one press, and
            it can be pasted to whoever is looking into it. */}
        <button
          type="button"
          className={s.callLog}
          onClick={() => {
            void navigator.clipboard
              ?.writeText(voiceLogText())
              .then(() => setCopied(true))
              .catch(() => setCopied(false));
          }}
        >
          {copied ? t("Voice log copied") : t("Copy voice log")}
        </button>
      </div>
    </div>
  );
}
